import { useEffect, useRef } from 'react';
import * as maplibregl from 'maplibre-gl';
import { MAP_STYLE_URL, circlePolygonCoords } from '../lib/mapLibre';

// Single-marker MapLibre map for a traffic incident's location, mirroring the imperative
// dynamic-init pattern used by RelayMap.tsx/FleetMapPage.tsx (self-hosted vector tiles, no API
// key required) — just one marker + a highlighted radius circle instead of a multi-point
// fleet/route map.
export function TrafficIncidentMap({ lat, lng, title }: { lat: number; lng: number; title: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE_URL,
      center: [lng, lat],
      zoom: 13,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }));

    map.on('load', () => {
      new maplibregl.Marker()
        .setLngLat([lng, lat])
        .setPopup(new maplibregl.Popup().setHTML(`<b>${title}</b>`))
        .addTo(map);

      map.addSource('incident-radius', {
        type: 'geojson',
        data: {
          type: 'Feature', properties: {},
          geometry: { type: 'Polygon', coordinates: [circlePolygonCoords(lat, lng, 400)] },
        },
      });
      map.addLayer({
        id: 'incident-radius-fill', type: 'fill', source: 'incident-radius',
        paint: { 'fill-color': '#dc2626', 'fill-opacity': 0.15 },
      });
      map.addLayer({
        id: 'incident-radius-line', type: 'line', source: 'incident-radius',
        paint: { 'line-color': '#dc2626', 'line-width': 1.5 },
      });
    });

    setTimeout(() => map.resize(), 150);

    return () => { mapRef.current?.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={containerRef} data-testid="traffic-news-detail-map" className="w-full h-64 rounded-2xl overflow-hidden" />;
}
